/**
 * The company folder tree in the remote store (spec section 12). All folders
 * are created by the app; ids are cached in `driveState.folders` and re-found
 * by name when a cached id no longer resolves.
 *
 *   EgenBalans/
 *     LÄS MIG.txt                    (the adapter's `readme`)
 *     <Namn> (<orgnr>)/
 *       bokforing.json, Systemdokumentation.md
 *       arkiv/  Inkorg/  Kvitton/<år>/  Ej bokfört/<år>/  Kontoutdrag/<år>/  Bokslut/<år>/
 *
 * Provider-independent: every call goes through the `RemoteStore` port, so the
 * same tree is built on Drive, on a WebDAV/S3 backend or in a local folder.
 * The `driveState` table keeps its name for now — renaming it is a Dexie
 * migration, not part of extracting the port.
 */
import { getRemoteStore, putText, unusedName, type RemoteEntry, type RemoteId } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { emptyDriveState } from '@/domain/companies/defaults';
import type { Company, DriveFolders, DriveState, Uuid } from '@/domain/types';

export const ROOT_FOLDER_NAME = 'EgenBalans';
export const SNAPSHOT_FILE_NAME = 'bokforing.json';
export const SYSTEM_DOC_FILE_NAME = 'Systemdokumentation.md';

export const SUBFOLDER_NAMES = {
  inbox: 'Inkorg',
  kvitton: 'Kvitton',
  ejBokfort: 'Ej bokfört',
  kontoutdrag: 'Kontoutdrag',
  arkiv: 'arkiv',
  bokslut: 'Bokslut',
} as const satisfies Record<Exclude<keyof DriveFolders, 'root' | 'company'>, string>;

/** `Firma AB (5560000000)`; a company without org.nr gets only its name. Slashes are not allowed in names. */
export function companyFolderName(company: Pick<Company, 'name' | 'orgNumber'>): string {
  const name = company.name.replace(/[/\\]/g, '-').trim() || 'Företag';
  return company.orgNumber ? `${name} (${company.orgNumber})` : name;
}

/** Legacy flat snapshot name from the v1 app: `EgenBalans/<orgnr>.json`. */
export function legacySnapshotName(company: Pick<Company, 'id' | 'orgNumber'>): string {
  return `${company.orgNumber || company.id}.json`;
}

/** Read-modify-write on the company's DriveState row (created if missing). */
export async function updateDriveState(companyId: Uuid, mutate: (s: DriveState) => void, db: BookkeepingDB = defaultDb): Promise<DriveState> {
  return db.transaction('rw', db.driveState, async () => {
    const state = (await db.driveState.get(companyId)) ?? emptyDriveState(companyId);
    mutate(state);
    await db.driveState.put(state);
    return state;
  });
}

export async function getDriveState(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<DriveState> {
  return (await db.driveState.get(companyId)) ?? emptyDriveState(companyId);
}

async function folderAlive(folderId: RemoteId): Promise<boolean> {
  const entry = await getRemoteStore().stat(folderId);
  return !!entry && !entry.trashed;
}

/** Find or create the root folder and the adapter's "LÄS MIG" file. */
export async function ensureRootFolder(): Promise<RemoteEntry> {
  const store = getRemoteStore();
  const root = await store.ensureFolder(ROOT_FOLDER_NAME);
  if (store.readme && !(await store.findByName(store.readme.name, root.id))) {
    await putText(store, { name: store.readme.name, parentId: root.id, text: store.readme.text, mediaType: 'text/plain; charset=UTF-8' });
  }
  return root;
}

async function buildTree(company: Company, cached: DriveFolders | null): Promise<DriveFolders> {
  const store = getRemoteStore();
  const root = cached && (await folderAlive(cached.root)) ? { id: cached.root } : await ensureRootFolder();
  const companyFolder =
    cached && (await folderAlive(cached.company)) ? { id: cached.company } : await store.ensureFolder(companyFolderName(company), root.id);
  const sub = async (key: keyof typeof SUBFOLDER_NAMES): Promise<string> => {
    const id = cached?.[key];
    if (id && cached.company === companyFolder.id && (await folderAlive(id))) return id;
    return (await store.ensureFolder(SUBFOLDER_NAMES[key], companyFolder.id)).id;
  };
  return {
    root: root.id,
    company: companyFolder.id,
    inbox: await sub('inbox'),
    kvitton: await sub('kvitton'),
    ejBokfort: await sub('ejBokfort'),
    kontoutdrag: await sub('kontoutdrag'),
    arkiv: await sub('arkiv'),
    bokslut: await sub('bokslut'),
  };
}

const treeInflight = new Map<Uuid, Promise<DriveFolders>>();

/**
 * Return the company's folder ids, creating missing folders. The cached ids
 * are trusted when `verify` is false (default for hot paths); pass
 * `{ verify: true }` on app open or after a not-found error to re-find stale
 * ids by name.
 */
export async function ensureCompanyTree(company: Company, db: BookkeepingDB = defaultDb, opts: { verify?: boolean } = {}): Promise<DriveFolders> {
  const state = await getDriveState(company.id, db);
  if (state.folders && !opts.verify) return state.folders;
  const running = treeInflight.get(company.id);
  if (running) return running;
  const promise = (async () => {
    const folders = await buildTree(company, state.folders);
    await updateDriveState(company.id, (s) => void (s.folders = folders), db);
    return folders;
  })().finally(() => treeInflight.delete(company.id));
  treeInflight.set(company.id, promise);
  return promise;
}

/** Forget cached ids so the next `ensureCompanyTree` re-finds them by name. */
export async function invalidateCompanyTree(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
  await updateDriveState(companyId, (s) => void (s.folders = null), db);
  yearFolderCache.clear();
}

/**
 * Drop every handle that only means something to the previous backend.
 *
 * `driveState` caches ids the adapter minted — folder ids, the snapshot's id,
 * and the opaque `version` of the last backup. Point the port at a different
 * store and all three are nonsense there: Drive's file ids are not Koofr paths,
 * and a Drive timestamp compared against a Koofr one differs forever, which
 * would make every backup look like someone else's write and fail as a
 * conflict. Called when connecting or disconnecting a provider.
 *
 * Returns the companies that had a tree, because clearing `folders` also stops
 * the scheduler (`dueOnOpen` and `runAuto` both bail without one) — the caller
 * has to rebuild them on the new store or backups silently stop.
 */
export async function forgetRemoteLocations(db: BookkeepingDB = defaultDb): Promise<Uuid[]> {
  const states = await db.driveState.toArray();
  const connected = states.filter((s) => s.folders).map((s) => s.companyId);
  for (const state of states) {
    await updateDriveState(
      state.companyId,
      (s) => {
        s.folders = null;
        delete s.snapshotFileId;
        delete s.lastBackupModifiedTime;
        // The new store has no frozen copies yet, so let the next backup make one.
        delete s.lastMonthlyFrozen;
        delete s.lastError;
      },
      db,
    );
  }
  yearFolderCache.clear();
  return connected;
}

const yearFolderCache = new Map<string, string>();

/** `Kvitton/2026` style year sub-folder, created on demand and cached per session. */
export async function ensureYearFolder(parentId: RemoteId, year: string | number): Promise<string> {
  const key = `${parentId}/${year}`;
  const cached = yearFolderCache.get(key);
  if (cached) return cached;
  const folder = await getRemoteStore().ensureFolder(String(year), parentId);
  yearFolderCache.set(key, folder.id);
  return folder.id;
}

/** Year part of an ISO date; today's year when the date is missing. */
export function yearOf(date: string | undefined | null, today: () => string = () => new Date().toISOString()): string {
  const source = date && /^\d{4}/.test(date) ? date : today();
  return source.slice(0, 4);
}

/** The company's legacy flat snapshot in the root, if the v1 app left one. */
export async function findLegacySnapshot(company: Company, rootId: RemoteId): Promise<RemoteEntry | undefined> {
  return getRemoteStore().findByName(legacySnapshotName(company), rootId, 'file');
}

/**
 * First backup after upgrading: move `EgenBalans/<orgnr>.json` into the
 * company folder as `bokforing.json` so history is kept and the new snapshot
 * overwrites it. Returns the moved entry or undefined when there was none.
 */
export async function migrateLegacySnapshot(company: Company, folders: DriveFolders): Promise<RemoteEntry | undefined> {
  const store = getRemoteStore();
  const legacy = await findLegacySnapshot(company, folders.root);
  if (!legacy) return undefined;
  const existing = await store.findByName(SNAPSHOT_FILE_NAME, folders.company);
  if (existing) {
    // A v2 snapshot already exists; keep the legacy file next to it under a
    // distinct name rather than losing it.
    return store.move(legacy.id, folders.root, folders.arkiv, `legacy-${legacy.name}`);
  }
  return store.move(legacy.id, folders.root, folders.company, SNAPSHOT_FILE_NAME);
}

// ---- Company documents -------------------------------------------------------

async function companyOf(companyId: Uuid, db: BookkeepingDB): Promise<Company> {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error(`Company ${companyId} not found`);
  return company;
}

/** Create or overwrite `Systemdokumentation.md` in the company folder (period lock / year close). */
export async function uploadSystemDocumentation(companyId: Uuid, markdown: string, db: BookkeepingDB = defaultDb): Promise<RemoteEntry> {
  const store = getRemoteStore();
  const folders = await ensureCompanyTree(await companyOf(companyId, db), db);
  const existing = await store.findByName(SYSTEM_DOC_FILE_NAME, folders.company);
  return putText(store, {
    name: SYSTEM_DOC_FILE_NAME,
    parentId: folders.company,
    text: markdown,
    mediaType: 'text/markdown; charset=UTF-8',
    ...(existing ? { id: existing.id } : {}),
  });
}

/** Store an imported bank statement under `Kontoutdrag/<år>/`; returns the remote id. */
export async function uploadStatementFile(companyId: Uuid, file: Blob & { name?: string }, year: string | number, db: BookkeepingDB = defaultDb): Promise<string> {
  const folders = await ensureCompanyTree(await companyOf(companyId, db), db);
  const parent = await ensureYearFolder(folders.kontoutdrag, year);
  const uploaded = await getRemoteStore().put({
    name: file.name || `kontoutdrag-${year}.csv`,
    parentId: parent,
    blob: file,
    mediaType: file.type || 'application/octet-stream',
    metadata: { companyId, kind: 'statement' },
  });
  return uploaded.id;
}

/** Store a year-end document (SIE, Arkivutdrag, rapporter) under `Bokslut/<år>/`; never overwrites. */
export async function uploadBokslutFile(companyId: Uuid, i: { name: string; blob: Blob; mediaType: string; year: string | number }, db: BookkeepingDB = defaultDb): Promise<RemoteEntry> {
  const store = getRemoteStore();
  const folders = await ensureCompanyTree(await companyOf(companyId, db), db);
  const parent = await ensureYearFolder(folders.bokslut, i.year);
  const dot = i.name.lastIndexOf('.');
  const [base, extension] = dot > 0 ? [i.name.slice(0, dot), i.name.slice(dot)] : [i.name, ''];
  const name = await unusedName((candidate) => store.findByName(candidate, parent), (n) => (n === 1 ? i.name : `${base} (${n})${extension}`));
  return store.put({
    name,
    parentId: parent,
    blob: i.blob,
    mediaType: i.mediaType,
    metadata: { companyId, kind: 'bokslut' },
  });
}
